import classNames from 'classnames';
import React from 'react';
import type { ReactNode } from 'react';

import { type AriaOverlayProps, OverlayProvider } from '@react-aria/overlays';

import DialogChildren from './controls/dialog-children';
import type { IProps as IBodyProps } from './controls/dialog-children';
import DialogContainer from './controls/dialog-container';
import type { IProps as IContainerProps } from './controls/dialog-container';
import DialogFooter from './controls/dialog-footer';
import type { IProps as IFooterProps } from './controls/dialog-footer';
import DialogHeader from './controls/dialog-header';
import type { IProps as IHeaderProps } from './controls/dialog-header';

interface IProps
  extends IContainerProps,
    Omit<IBodyProps, 'className'>,
    Omit<IFooterProps, 'className'>,
    Omit<IHeaderProps, 'className'> {
  bodyClass?: string;
  displayFooter?: boolean;
  footer?: ReactNode;
  footerClass?: string;
  headerClass?: string;
  overlayClassName?: string;
  shouldCloseOnInteractOutside?: AriaOverlayProps['shouldCloseOnInteractOutside'];
}

export const NOOP = () => null;

const DialogAria: React.FC<React.PropsWithChildren<IProps>> = ({
  children,
  bodyClass,
  btnCancelClass,
  btnOkClass,
  btnDeleteClass,
  cancelText,
  closable,
  containerClass: propContainerClass,
  overlayClassName,
  disabledBtnOK,
  disabledBtnCancel,
  disabledBtnDelete,
  displayFooter,
  footer,
  footerClass,
  headerClass,
  isLoading,
  isSaving,
  okText,
  showCancel,
  showLoadingBtnOK,
  showSave,
  spinnerText,
  subTitle,
  title,
  onCancel,
  onClose,
  onOK,
  onDelete,
  ...otherProps
}) => {
  const containerClass = React.useMemo(() => {
    return classNames(
      'rounded-lg bg-gray-50 m-auto max-h-[calc(100vh-100px)] overflow-y-auto flex flex-col',
      propContainerClass
    );
  }, [propContainerClass]);

  return (
    <OverlayProvider>
      <DialogContainer
        {...otherProps}
        containerClass={containerClass}
        overlayClassName={overlayClassName}
        onClose={onClose ?? onCancel}
      >
        {!!title && (
          <DialogHeader
            className={headerClass}
            closable={closable}
            subTitle={subTitle}
            title={title}
            onClose={onClose}
            onCancel={onCancel}
          />
        )}
        <DialogChildren
          className={bodyClass}
          isLoading={isLoading}
          isSaving={isSaving}
          spinnerText={spinnerText}
        >
          {children}
        </DialogChildren>
        {displayFooter && (
          <DialogFooter
            btnCancelClass={btnCancelClass}
            btnDeleteClass={btnDeleteClass}
            btnOkClass={btnOkClass}
            cancelText={cancelText}
            className={footerClass}
            disabledBtnCancel={disabledBtnCancel || isLoading || isSaving}
            disabledBtnDelete={disabledBtnDelete || isLoading || isSaving}
            disabledBtnOK={disabledBtnOK || isLoading || isSaving}
            okText={okText}
            showCancel={showCancel}
            showSave={showSave}
            showLoadingBtnOK={showLoadingBtnOK}
            onCancel={!disabledBtnCancel ? onCancel ?? onClose : NOOP}
            onOK={!disabledBtnOK ? onOK : NOOP}
            onDelete={!disabledBtnDelete ? onDelete : NOOP}
          >
            {footer}
          </DialogFooter>
        )}
      </DialogContainer>
    </OverlayProvider>
  );
};

DialogAria.defaultProps = {
  closable: true,
  displayFooter: true
};

class Dialog extends React.Component<React.PropsWithChildren<IProps>> {
  render() {
    return <DialogAria {...this.props} />;
  }
}

export default Dialog;
